/**
 * Hook: Premium-Support-Chat aktive Konversation + Messages + Live-Updates
 *
 * Verantwortlich für die komplette Daten-Schicht des Chat-Panels:
 *   - Initial: lädt aktive Konversation + Messages via REST
 *   - Live: hört WS-Events 'message:new' und 'message:read', merged sie in den State
 *   - Optimistic Send: zeigt Nachricht sofort mit Status 'sending', tauscht
 *     gegen die echte Server-Antwort beim Echo
 *   - Mark-Read: bei Panel-Öffnung automatisch alle Admin-Nachrichten als gelesen
 *
 * Lifecycle:
 *   - enabled=false → Hook macht nichts (kein REST, kein WS-Subscribe)
 *   - enabled=true → initial-load + WS-Subscriptions
 *   - bei unmount: WS-Unsubscribe (Verbindung selbst bleibt für andere Konsumenten)
 *
 * Optimistic-UI-Status:
 *   - 'sending'  → User hat geklickt, Server-Echo steht noch aus (⏰)
 *   - 'sent'     → Server hat bestätigt (✓)
 *   - 'read'     → andere Seite hat gelesen (✓✓ in Brand-Gold)
 *   - 'failed'   → Server-Fehler, Retry möglich (✕)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchActiveConversation,
  fetchMessages,
  sendMessage as apiSendMessage,
  sendMessageWithImages as apiSendMessageWithImages,
  markConversationRead as apiMarkRead,
  type ChatMessage,
  type ConversationSummary
} from '../services/support/chatService';
import { compressImage } from '../services/support/imageCompression';
import { useChatWebSocket, type ChatServerEvent } from './useChatWebSocket';

// ============================================================================
// TYPES
// ============================================================================

export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read' | 'failed';

/**
 * UI-erweiterte Message: zusätzlich Status für Optimistic UI + ein
 * temporäres clientId, damit Optimistic-Echo die richtige Nachricht updaten kann.
 */
export interface UIMessage extends Omit<ChatMessage, 'id'> {
  /** Server-ID, wenn vorhanden — sonst leer (während sending) */
  id: string;
  /** Client-side temp-ID für Optimistic-Matching. Bleibt erhalten nach Echo. */
  clientId?: string;
  status: MessageStatus;
}

interface UseChatConversationOptions {
  enabled?: boolean;
  /** Wenn true: bei jedem neuen Admin-Event automatisch markRead aufrufen. */
  autoMarkRead?: boolean;
}

interface UseChatConversationResult {
  conversation: ConversationSummary | null;
  messages: UIMessage[];
  isLoading: boolean;
  error: Error | null;
  /**
   * Sendet eine Nachricht (Optimistic). Optional bis zu 3 Bilder + jeweilige
   * Captions + optional eine Reply-Referenz auf eine andere Nachricht.
   */
  send: (
    content: string,
    images?: File[],
    captions?: (string | null)[],
    replyToMessageId?: string | null
  ) => Promise<void>;
  /** Re-tries eine fehlgeschlagene Nachricht (anhand clientId). */
  retry: (clientId: string) => Promise<void>;
}

// ============================================================================
// HOOK
// ============================================================================

export function useChatConversation(options: UseChatConversationOptions = {}): UseChatConversationResult {
  const enabled = options.enabled !== false;
  const autoMarkRead = options.autoMarkRead !== false;

  const [conversation, setConversation] = useState<ConversationSummary | null>(null);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Aktuelle Konversations-ID in einer Ref halten — WS-Handler brauchen sie
  // ohne neue Subscribe-Subscriptions auszulösen, wenn sich die ID ändert.
  const conversationIdRef = useRef<string | null>(null);

  // Aktueller messages-Snapshot in einer Ref — der send()-useCallback hat
  // [] als Dependency-Array (für stabile Identität). Wir brauchen aber Zugriff
  // auf den aktuellen State um das Reply-Snippet aus der zitierten Nachricht
  // zu lesen. Eine Ref löst das ohne den Callback neu zu erzeugen.
  const messagesRef = useRef<UIMessage[]>([]);

  const { subscribe, sendClientEvent } = useChatWebSocket({ enabled });

  // messagesRef immer auf dem neuesten Stand halten, damit send() das
  // Reply-Snippet aus dem aktuellen State holen kann.
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // ========================================================================
  // INITIAL LOAD (REST)
  // ========================================================================
  useEffect(() => {
    if (!enabled) {
      // Kein Reset hier — wenn enabled vorübergehend false wird (z.B. Logout),
      // räumt der Auth-Watcher auf.
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const conv = await fetchActiveConversation();
        if (cancelled) return;
        conversationIdRef.current = conv.id;
        setConversation(conv);

        const { messages: loadedMessages } = await fetchMessages(conv.id);
        if (cancelled) return;

        setMessages(
          loadedMessages.map((m): UIMessage => ({
            ...m,
            status: m.readAt ? 'read' : m.deliveredAt ? 'delivered' : 'sent'
          }))
        );

        // Bulk-mark-delivered für Admin-Nachrichten ohne delivered_at — passiert
        // z.B. wenn der User offline war als der Admin schrieb, dann reconnected.
        const hasUndeliveredAdminMsgs = loadedMessages.some(
          (m) => m.senderType === 'admin' && !m.deliveredAt
        );
        if (hasUndeliveredAdminMsgs) {
          sendClientEvent({ type: 'mark_delivered', conversationId: conv.id });
        }

        // Auto-mark-read beim initialen Load (User schaut ja gerade hin)
        if (autoMarkRead && conv.unreadCountUser > 0) {
          await apiMarkRead(conv.id);
          // Lokaler State: Counter resetten + alle Admin-Nachrichten auf 'read'
          setConversation((prev) => (prev ? { ...prev, unreadCountUser: 0 } : prev));
          setMessages((prev) =>
            prev.map((m) =>
              m.senderType === 'admin' && m.status === 'sent'
                ? { ...m, status: 'read', readAt: new Date().toISOString() }
                : m
            )
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Konversation konnte nicht geladen werden'));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, autoMarkRead]);

  // ========================================================================
  // WS-EVENT-SUBSCRIPTION: message:new
  // ========================================================================
  useEffect(() => {
    if (!enabled) return;

    const unsubNew = subscribe('message:new', (event) => {
      // Nur Events der aktuellen Konversation verarbeiten
      if (event.conversationId !== conversationIdRef.current) return;

      setMessages((prev) => {
        // 1. Server-ID bereits im State → Echo, ignorieren
        if (prev.some((m) => m.id === event.message.id)) {
          return prev;
        }

        // 2. Race-Condition-Schutz: WS-Echo kann VOR der REST-Antwort
        //    ankommen. In dem Fall ist im State noch eine optimistic-Message
        //    (clientId 'temp-...') mit gleichem Inhalt + senderType. Statt
        //    eine zweite Bubble hinzuzufügen, ersetzen wir die Optimistic.
        //    Wenn die REST-Antwort später eintrifft, findet sie die
        //    clientId nicht mehr → setzt nichts (siehe send()).
        if (event.message.senderType === 'user') {
          const matchIdx = prev.findIndex(
            (m) =>
              m.clientId !== undefined &&
              m.id.startsWith('temp-') &&
              m.senderType === 'user' &&
              m.content === event.message.content
          );
          if (matchIdx !== -1) {
            const next = [...prev];
            next[matchIdx] = {
              ...next[matchIdx]!,
              id: event.message.id,
              status: 'sent',
              createdAt: event.message.createdAt
            };
            return next;
          }
        }

        const incoming: UIMessage = {
          id: event.message.id,
          conversationId: event.conversationId,
          senderType: event.message.senderType,
          senderId: null,
          content: event.message.content,
          contentType: event.message.contentType,
          isInternalNote: false,
          deliveredAt: null,
          readAt: null,
          editedAt: null,
          createdAt: event.message.createdAt,
          attachments: (event.message.attachments ?? []).map((a) => ({ ...a, caption: a.caption ?? null })),
          replyTo: event.message.replyTo ?? null,
          status: 'sent'
        };
        return [...prev, incoming];
      });

      // Bei eingehender Admin-Nachricht: sofort delivered-ACK an Server schicken,
      // damit der Admin die ✓✓-Häkchen für seine Nachricht sieht. Das passiert
      // unabhängig vom Read-Status (Browser hat das Frame empfangen).
      if (event.message.senderType === 'admin' && conversationIdRef.current) {
        sendClientEvent({ type: 'mark_delivered', conversationId: conversationIdRef.current });
      }

      // Bei Admin-Event + autoMarkRead: sofort markieren (User schaut hin)
      if (event.message.senderType === 'admin' && autoMarkRead && conversationIdRef.current) {
        const convId = conversationIdRef.current;
        // Fire-and-forget. Bei Erfolg löst das Backend ein 'message:read'-Event aus,
        // das wiederum den State updated.
        void apiMarkRead(convId).catch(() => {
          /* still ok if it fails — User kann manuell schließen + öffnen */
        });
      }
    });

    return () => unsubNew();
  }, [enabled, subscribe, autoMarkRead, sendClientEvent]);

  // ========================================================================
  // WS-EVENT-SUBSCRIPTION: message:delivered (3-Stufen-Häkchen)
  // ========================================================================
  useEffect(() => {
    if (!enabled) return;

    const unsubDelivered = subscribe('message:delivered', (event) => {
      if (event.conversationId !== conversationIdRef.current) return;

      // event.deliveredByType ist die Seite, die EMPFANGEN hat. Aus User-Sicht
      // interessant ist nur "Admin hat empfangen" → eigene User-Nachrichten
      // upgraden auf 'delivered'.
      if (event.deliveredByType !== 'admin') return;

      const ids = new Set(event.messageIds);
      setMessages((prev) =>
        prev.map((m) => {
          if (m.senderType !== 'user') return m;
          if (!ids.has(m.id)) return m;
          // Wenn die Nachricht schon 'read' ist: nicht zurückstufen.
          if (m.status === 'read') return m;
          return { ...m, status: 'delivered', deliveredAt: event.deliveredAt };
        })
      );
    });

    return () => unsubDelivered();
  }, [enabled, subscribe]);

  // ========================================================================
  // WS-EVENT-SUBSCRIPTION: message:read
  // ========================================================================
  useEffect(() => {
    if (!enabled) return;

    const unsubRead = subscribe('message:read', (event) => {
      if (event.conversationId !== conversationIdRef.current) return;

      // Wenn der ANDERE Teilnehmer gelesen hat: alle eigenen Nachrichten auf 'read' setzen
      // (Doppelhaken). Wenn ICH gelesen habe: alle Gegenseite-Nachrichten auf 'read'.
      const otherType: 'user' | 'admin' = event.readByType === 'user' ? 'user' : 'admin';

      setMessages((prev) =>
        prev.map((m) => {
          // "Andere Seite hat MEINE Nachrichten gelesen" → status='read' auf Nachrichten,
          // die NICHT vom Reader stammen. Auch 'delivered' ist gültiger
          // Vorgängerstatus (3-Stufen-Flow: sent → delivered → read).
          if (
            m.senderType !== otherType &&
            (m.status === 'sent' || m.status === 'sending' || m.status === 'delivered')
          ) {
            return { ...m, status: 'read', readAt: event.readAt };
          }
          return m;
        })
      );
    });

    return () => unsubRead();
  }, [enabled, subscribe]);

  // ========================================================================
  // SEND (Optimistic UI)
  // ========================================================================
  const send = useCallback(async (
    content: string,
    images?: File[],
    captions?: (string | null)[],
    replyToMessageId?: string | null
  ): Promise<void> => {
    const trimmed = content.trim();
    const hasImages = images !== undefined && images.length > 0;
    if (!trimmed && !hasImages) return;

    const convId = conversationIdRef.current;
    if (!convId) {
      throw new Error('Keine aktive Konversation');
    }

    if (hasImages && images!.length > 3) {
      throw new Error('Maximal 3 Bilder pro Nachricht.');
    }

    const clientId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    // Optimistic Attachments: lokale ObjectURLs für sofortige Vorschau,
    // werden beim Server-Echo durch echte URLs ersetzt.
    const optimisticAttachments = hasImages
      ? images!.map((file, i) => ({
          id: `${clientId}-att-${i}`,
          storagePath: URL.createObjectURL(file),
          fileName: file.name,
          mimeType: file.type,
          fileSize: file.size,
          width: null,
          height: null,
          caption: (captions?.[i] ?? '').trim() || null
        }))
      : [];

    // Optimistic-Reply-Snippet aus dem aktuellen State holen, damit die
    // sofort gerenderte Bubble bereits das Zitat zeigt. Wenn die zitierte
    // Nachricht nicht im State ist (sehr alt, abgepaginiert), zeigen wir
    // null — der Server-Echo überschreibt das Snippet ohnehin.
    let optimisticReplyTo: UIMessage['replyTo'] = null;
    if (replyToMessageId) {
      // setMessages-Closure würde stale lesen — daher pre-read via Closure-Snapshot
      // ist ok, da wir gleich danach setState aufrufen und der Funktional-Updater
      // mit dem aktuellen Wert läuft.
      const target = messagesRef.current.find((m) => m.id === replyToMessageId);
      if (target) {
        const previewSrc = target.content.trim();
        optimisticReplyTo = {
          id: target.id,
          senderType: target.senderType,
          contentPreview: previewSrc.length > 80 ? previewSrc.slice(0, 79) + '…' : previewSrc,
          hasAttachments: target.attachments.length > 0
        };
      }
    }

    const optimistic: UIMessage = {
      id: clientId,
      clientId,
      conversationId: convId,
      senderType: 'user',
      senderId: null,
      content: trimmed,
      contentType: hasImages ? 'image' : 'text',
      isInternalNote: false,
      deliveredAt: null,
      readAt: null,
      editedAt: null,
      createdAt: new Date().toISOString(),
      attachments: optimisticAttachments,
      replyTo: optimisticReplyTo,
      status: 'sending'
    };

    setMessages((prev) => [...prev, optimistic]);

    try {
      let serverMsg: ChatMessage;
      if (hasImages) {
        // Client-seitig komprimieren (parallel — sind unabhängig)
        const compressed = await Promise.all(images!.map((f) => compressImage(f)));
        serverMsg = await apiSendMessageWithImages(
          convId,
          trimmed,
          compressed.map((c) => c.file),
          (captions ?? []).map((c) => (c ?? '').trim() || null),
          replyToMessageId ?? null
        );
      } else {
        serverMsg = await apiSendMessage(convId, trimmed, replyToMessageId ?? null);
      }

      // Optimistic-Eintrag durch Server-Antwort ersetzen — ObjectURLs revoken
      // damit kein Memory-Leak.
      for (const att of optimisticAttachments) {
        try { URL.revokeObjectURL(att.storagePath); } catch { /* ignore */ }
      }

      setMessages((prev) =>
        prev.map((m) =>
          m.clientId === clientId
            ? { ...serverMsg, clientId, status: 'sent' as MessageStatus }
            : m
        )
      );
    } catch (err) {
      console.error('[Chat] sendMessage failed', err);
      setMessages((prev) =>
        prev.map((m) => (m.clientId === clientId ? { ...m, status: 'failed' as MessageStatus } : m))
      );
    }
  }, []);

  const retry = useCallback(
    async (clientId: string): Promise<void> => {
      const target = messages.find((m) => m.clientId === clientId);
      if (!target || target.status !== 'failed') return;

      // Failed-Eintrag entfernen, neu senden
      setMessages((prev) => prev.filter((m) => m.clientId !== clientId));
      await send(target.content);
    },
    [messages, send]
  );

  return { conversation, messages, isLoading, error, send, retry };
}
